import { Link } from '@/components/atoms/Link';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import { ArticleCard } from '@/components/organisms/ArticleCard';
import { Category } from '@/services/wordpress/categories';
import { BlogPreview } from '@/types/blog';

export interface ArticlesByTagTemplateProps {
  tag: { name: string; slug: string };
  categories: Omit<Category, 'count'>[];
  articles: BlogPreview[];
}

/**
 * /news/tag/[slug]: same layout as the /news/category/[slug] page (gradient
 * header + breadcrumb, row ArticleCards, Categories side column).
 */
export const ArticlesByTagTemplate: React.FC<ArticlesByTagTemplateProps> = ({
  tag,
  categories,
  articles,
}) => {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-col gap-3xl bg-gradient-to-r from-lightTheme-start to-lightTheme-end px-xl pb-3xl pt-xl">
        <div className="py-m">
          <Breadcrumb
            links={[
              { text: 'Home', href: '/' },
              { text: 'News', href: '/news' },
              { text: `#${tag.name}`, href: `/news/tag/${tag.slug}` },
            ]}
          />
        </div>
        <div className="flex flex-col gap-s">
          <h1 className="text-h2SemiBold">#{tag.name}</h1>
          <p className="text-body1Regular text-neutral-800">
            {articles.length > 0
              ? `Latest EV news tagged #${tag.name}`
              : `No articles tagged #${tag.name} yet`}
          </p>
        </div>
      </div>
      <div className="flex flex-col bg-neutral-100 md:flex-row md:gap-xl">
        <div className="flex flex-col gap-l bg-neutral-100 px-l pb-3xl pt-xl md:w-2/3">
          {articles.length > 0 ? (
            <div className="flex flex-col gap-xl">
              {articles.map((article) => (
                <ArticleCard layout="row" key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-m rounded-lg bg-white p-xl">
              <p className="text-h5Medium">
                We couldn&apos;t find any articles tagged #{tag.name}.
              </p>
              <p className="text-body1Regular text-neutral-800">
                Browse the latest stories or pick a category instead.
              </p>
              <Link
                href="/news"
                className="inline-flex min-h-[44px] items-center rounded-full bg-blue-medium px-xl text-body1Medium text-white hover:bg-blue-dark"
              >
                See all news
              </Link>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-l bg-white px-xl py-l md:flex-1 md:bg-neutral-100">
          <div>
            <h3 className="text-h3SemiBold">Categories</h3>
          </div>
          <div className="flex flex-wrap gap-x-l gap-y-s">
            {categories.map((category) => (
              <Link
                href={`/news/category/${category.slug}`}
                key={category.id}
                className="inline-flex min-h-[40px] items-center"
              >
                <Badge
                  variant={BadgeVariants.Blue}
                  label={category.name}
                  onClick={() => undefined}
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
